import React from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/ui/components/ui/chart";

interface CycleData {
  time: string;
  cycleTime: number;
  waitTimeValue: number;
  status: "normal" | "warning" | "critical";
  bucketCount: number;
  bouldersPresent: boolean;
  waitTimeText?: string;
}

interface Props {
  data: CycleData[];
  targetTime: number;
  averageTime: number;
  width?: number;
  height?: number;
  onBarClick?: (data: CycleData, index: number) => void;
  activeIndex?: number | null;
}

const MachineCycleChart: React.FC<Props> = ({
  data,
  targetTime,
  averageTime,
  width = 1200,
  height = 500,
  onBarClick,
  activeIndex,
}) => {
  const { t } = useTranslation("machines");
  const pseudoLogTicks = [-80, -40, -20, -10, -5, -2, -1, 0, 1, 2, 5, 10, 20];
  const zeroIndex = pseudoLogTicks.indexOf(0); // Index 7

  // Transform actual values to pseudo-log scale positions (0-12 range matching tick indices)
  const getInterpolatedIndex = (value: number): number => {
    const lower = Math.max(
      ...pseudoLogTicks.filter((v) => v <= value),
      pseudoLogTicks[0]
    );
    const upper = Math.min(
      ...pseudoLogTicks.filter((v) => v >= value),
      pseudoLogTicks[pseudoLogTicks.length - 1]
    );

    if (lower === upper) {
      return pseudoLogTicks.indexOf(lower);
    }

    const lowerIndex = pseudoLogTicks.indexOf(lower);
    const upperIndex = pseudoLogTicks.indexOf(upper);
    const ratio = (value - lower) / (upper - lower);

    return lowerIndex + ratio * (upperIndex - lowerIndex);
  };

  // Transform data for Recharts
  // The Y-axis will be index-based (0 at center for value=0)
  // Negative indices for wait times below zero, positive for cycle times above zero
  const chartData = data.map((d) => {
    const waitIndex = getInterpolatedIndex(d.waitTimeValue);
    const cycleIndex = getInterpolatedIndex(Math.max(0.01, d.cycleTime));

    return {
      ...d,
      // Wait time: convert to negative offset from zero
      waitTime: -(waitIndex - zeroIndex),
      // Cycle time: convert to positive offset from zero
      cycleTime: cycleIndex - zeroIndex,
      // Keep original values for tooltip
      originalCycleTime: d.cycleTime,
      originalWaitTime: d.waitTimeValue,
    };
  });

  // Transform target and average to pseudo-log scale indices
  const targetTransformed = getInterpolatedIndex(targetTime);
  const averageTransformed = getInterpolatedIndex(averageTime);

  const colorMap = {
    normal: "#22c55e",
    warning: "#f97316",
    critical: "#ef4444",
  } as const;

  const chartConfig = {
    waitTime: {
      label: t("liveChart.series.waitTime"),
      color: "#3b82f6",
    },
    cycleTime: {
      label: t("liveChart.series.cycleTime"),
    },
  };

  // Calculate tick interval for x-axis
  const dataLength = data.length;
  const minTickSpacing = 40;
  const maxTicks = Math.max(3, Math.floor(width / minTickSpacing));
  const tickInterval = Math.max(1, Math.ceil(dataLength / maxTicks));

  // Custom tooltip content
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as CycleData & {
      originalCycleTime: number;
      originalWaitTime: number;
    };

    return (
      <div className="rounded-lg border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md">
        <div className="font-semibold">{data.time}</div>
        <div className="mt-1 space-y-0.5">
          <div>{t("liveChart.tooltip.cycleTime", { value: formatNumber(data.originalCycleTime, { maximumFractionDigits: 1 }) })}</div>
          <div>{t("liveChart.tooltip.bucketCount", { value: formatNumber(data.bucketCount) })}</div>
          {data.bouldersPresent && <div>{t("liveChart.tooltip.bouldersPresent")}</div>}
          {data.waitTimeText && <div>{t("liveChart.tooltip.waitTime", { value: data.waitTimeText })}</div>}
        </div>
      </div>
    );
  };

  return (
    <ChartContainer
      config={chartConfig}
      className="w-full h-full"
      style={{ height: `${height}px`, aspectRatio: 'auto' }}
    >
      <ComposedChart
        data={chartData}
        margin={{ top: 10, right: 20, bottom: 30, left: 0 }}
        onClick={(state: any) => {
          if (onBarClick && state?.activeTooltipIndex != null && data[state.activeTooltipIndex]) {
            onBarClick(data[state.activeTooltipIndex], state.activeTooltipIndex);
          }
        }}
        style={onBarClick ? { cursor: "pointer" } : undefined}
      >
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />

          <XAxis
            dataKey="time"
            tick={{ fontSize: 10, fill: "hsl(var(--foreground))" }}
            stroke="hsl(var(--border))"
            interval={tickInterval - 1}
          />

          <YAxis
            domain={[-zeroIndex, pseudoLogTicks.length - 1 - zeroIndex]}
            ticks={pseudoLogTicks.map((_, i) => i - zeroIndex)}
            tickFormatter={(value) => {
              const index = value + zeroIndex;
              return pseudoLogTicks[index]?.toString() || "";
            }}
            tick={{ fontSize: 12, fill: "hsl(var(--foreground))" }}
            stroke="hsl(var(--border))"
          />

          {/* Zero reference line */}
          <ReferenceLine
            y={0}
            stroke="hsl(var(--border))"
            strokeWidth={1}
          />

          {/* Target line (orange dashed) */}
          <ReferenceLine
            y={targetTransformed - zeroIndex}
            stroke="orange"
            strokeDasharray="4 2"
            strokeWidth={2}
          />

          {/* Average line (green solid) */}
          <ReferenceLine
            y={averageTransformed - zeroIndex}
            stroke="#22c55e"
            strokeWidth={2}
          />

          {/* Wait time bars (blue, going down from zero) */}
          <Bar
            dataKey="waitTime"
            fill="#3b82f6"
            barSize={20}
            radius={[4, 4, 4, 4]}
          />

          {/* Cycle time bars (colored based on status, going up from zero) */}
          <Bar
            dataKey="cycleTime"
            barSize={20}
            radius={[4, 4, 4, 4]}
            onClick={(_data: any, index: number) => {
              if (onBarClick && data[index]) {
                onBarClick(data[index], index);
              }
            }}
            cursor={onBarClick ? "pointer" : undefined}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colorMap[entry.status]} />
            ))}
          </Bar>

          <ChartTooltip content={<CustomTooltip />} />

          {/* Current timeline position indicator (red vertical line) - rendered last to be on top */}
          {activeIndex != null && activeIndex >= 0 && activeIndex < chartData.length && (
            <ReferenceLine
              x={chartData[activeIndex].time}
              stroke="#ef4444"
              strokeWidth={3}
              ifOverflow="extendDomain"
              isFront={true}
            />
          )}
        </ComposedChart>
      </ChartContainer>
  );
};

export default MachineCycleChart;
